import { AttachmentType, BeneficiaryVisibilityType } from "@/modules/beneficiary/common/enum";
import type {
  IBeneficiaryItem,
  TBeneficiaryAttachmentItem
} from "@/modules/beneficiary/common/types";
import { AttachModal } from "@/modules/beneficiary/components/CreateBeneficiary/AttachModal";
import { DeleteBeneficiaryModal } from "@/modules/beneficiary/components/DeleteBeneficiaryModal";
import { RenameBeneficiaryModal } from "@/modules/beneficiary/components/RenameBeneficiaryModal";
import {
  useAddBeneficiaryAccountMutation,
  useAddBeneficiaryCardMutation,
  useChangeVisibilityMutation,
  useGetBeneficiaryByIdQuery,
  useUpdateBeneficiaryAccountMutation,
  useUpdateBeneficiaryCardMutation,
} from "@/modules/beneficiary/services";
import {
  AppPaths,
  BeneficiaryType,
  CloudflareAsset,
  SearchParamKeys,
  SideSheetPosition
} from "@/shared/common/enums";
import { CloudFlareImage, EmptyOrErrorContent } from "@/shared/components";
import { useMediaQuery, useNotify } from "@/shared/hooks";
import { Button } from "@ab.uitools/ui-library/components/Button";
import { SideSheet } from "@ab.uitools/ui-library/components/SideSheet";
import type { ISVGIconProps } from "@ab.uitools/ui-library/components/SVGIcons/types";
import { Switcher } from "@ab.uitools/ui-library/components/Switcher";
import { Tab } from "@ab.uitools/ui-library/components/Tab";
import type { TTabItem } from "@ab.uitools/ui-library/components/Tab/types";
import { Text } from "@ab.uitools/ui-library/components/Text";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import uuid4 from "uuid4";

import { BeneficiaryAccountCardItem } from "./BeneficiaryAccountCardItem";
import { DeleteAccountCardModal } from "./DeleteAccountCardModal";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  beneficiaryId: string;
  beneficiaryType: string;
  refetchBeneficiariesList: () => void;
}

enum AttachModalState {
  Edit = "edit",
  Attach = "attach",
}

type TSelectedEntry = (IBeneficiaryItem & { id: string }) | null;

type TAttachModalState =
  | { mode: AttachModalState.Edit; item: IBeneficiaryItem & { id: string } }
  | { mode: AttachModalState.Attach }
  | null;

export const BeneficiaryDetails = ({
  isOpen,
  onClose,
  beneficiaryId,
  beneficiaryType,
  refetchBeneficiariesList,
}: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isDesktop = useMediaQuery();
  const { showErrorSnackbar, showSuccessSnackbar } = useNotify();
  const [activeTab, setActiveTab] = useState<string>(AttachmentType.Account);
  const [isDeleteOpen, setIsDeleteOpen] = useState<boolean>(false);
  const [isRenameOpen, setIsRenameOpen] = useState<boolean>(false);
  const [entryId, setEntryId] = useState<string | null>(null);
  const [attachmentToDelete, setAttachmentToDelete] = useState<TSelectedEntry>(null);
  const [attachModalState, setAttachModalState] = useState<TAttachModalState>(null);
  const [modalClientId, setModalClientId] = useState<string>("");
  const [isAvailableForOrgUsers, setIsAvailableForOrgUsers] = useState<boolean>();

  const [addBeneficiaryAccount] = useAddBeneficiaryAccountMutation();
  const [addBeneficiaryCard] = useAddBeneficiaryCardMutation();
  const [updateBeneficiaryAccount] = useUpdateBeneficiaryAccountMutation();
  const [updateBeneficiaryCard] = useUpdateBeneficiaryCardMutation();
  const [changeBeneficiaryVisibility] = useChangeVisibilityMutation();

  const {
    data: beneficiary,
    isFetching,
    refetch: refetchBeneficiaryById,
  } = useGetBeneficiaryByIdQuery(
    { beneficiaryId }, {
      refetchOnMountOrArgChange: true,
    });

  const isPrivate = beneficiaryType === BeneficiaryVisibilityType.Private;

  const tabItems: TTabItem[] = [
    {
      value: AttachmentType.Account,
      label: t("general.accounts"),
      disabled: false,
    },
    {
      value: AttachmentType.Card,
      label: t("general.cards"),
      disabled: false,
    },
  ];

  const activeItems = beneficiary?.entries.filter((item) => item.type.toLowerCase() === activeTab) || [];

  const onDeleteClick = () => {
    if (!beneficiary) return;
    setEntryId(beneficiary.id);
    setIsDeleteOpen(true);
  };

  const handleDeleteSuccess = () => {
    refetchBeneficiariesList();
    onClose();
  };

  const handleTransfer = (item: IBeneficiaryItem & { id: string }) => {
    const path =
      activeTab === AttachmentType.Account ? AppPaths.AccountTransfer : AppPaths.CardTransfer;
    navigate(`${path}?${SearchParamKeys.BeneficiaryNumber}=${encodeURIComponent(item.number)}`);
  };

  const openAttachModal = (item?: IBeneficiaryItem & { id: string }) => {
    setModalClientId(beneficiary?.beneficiaryClientCode || "");
    setAttachModalState(item ? {
      item,
      mode: AttachModalState.Edit,
    } : {
      mode: AttachModalState.Attach,
    });
  };

  const handleModalConfirm = (data: TBeneficiaryAttachmentItem) => {
    if (attachModalState?.mode === AttachModalState.Edit) {
      const { item } = attachModalState;
      const entry = { ...item, note: data.note, number: data.number };
      const update =
        activeTab === AttachmentType.Account ? updateBeneficiaryAccount : updateBeneficiaryCard;
      update({ beneficiaryId, beneficiaryEntryId: item.id, entry })
        .unwrap()
        .then(() => {
          showSuccessSnackbar(t("alerts.beneficiaryHasBeenSuccessfullyUpdated"));
          refetch();
        })
        .catch((err) => {
          showErrorSnackbar(err);

        })
        .finally(() => {
          setAttachModalState(null);
        });
    } else {
      const entry = {
        number: data.number,
        note: data.note,
        type: activeTab === AttachmentType.Account ? BeneficiaryType.Account : BeneficiaryType.Card,
        id: "",
      } as IBeneficiaryItem & { id: string };
      const add = activeTab === AttachmentType.Account ? addBeneficiaryAccount : addBeneficiaryCard;
      add({ beneficiaryId, entry, idempotencyKey: uuid4() })
        .unwrap()
        .then(() => {
          showSuccessSnackbar(t("alerts.beneficiaryHasBeenSuccessfullyAttached"));
          refetch();
        })
        .catch((err) => {
          showErrorSnackbar(err);
        })
        .finally(() => {
          setAttachModalState(null);
        });
    }
  };

  const accountEntries = (beneficiary?.entries.filter(
    (e) => e.type.toLowerCase() === AttachmentType.Account
  ) ?? []) as TBeneficiaryAttachmentItem[];

  const cardEntries = (beneficiary?.entries.filter(
    (e) => e.type.toLowerCase() === AttachmentType.Card
  ) ?? []) as TBeneficiaryAttachmentItem[];

  const refetch = () => {
    refetchBeneficiaryById();
    refetchBeneficiariesList();
  };

  const onChangeVisibility = (visibility: boolean) => {
    if (!isPrivate || !beneficiary?.id) return;
    setIsAvailableForOrgUsers(visibility);
    changeBeneficiaryVisibility({
      id: beneficiary?.id,
      isAvailableForOrgUsers: visibility,
    }).unwrap().catch(() => {
      setIsAvailableForOrgUsers(!visibility);
    });
  };

  useEffect(() => {
    setIsAvailableForOrgUsers(beneficiary?.isAvailableForOrgUsers);
  }, [beneficiary]);

  useEffect(() => {
    setActiveTab(AttachmentType.Account);
  }, [beneficiaryId]);

  return (
    <>
      <SideSheet
        position={isDesktop ? SideSheetPosition.Right : SideSheetPosition.Bottom}
        isPositioned={!isDesktop}
        withOverlay={!isDesktop}
        isOpen={isOpen}
        isBodyHidden={false}
        onClose={onClose}
        isWithDivider
        isLoading={isFetching}
        className="detail-side-sheet"
        size="small"
        topRightActions={
          isPrivate ? (
            <div className="flexbox align-items--center">
              <Button
                type="tertiary"
                size="small"
                data-testid="rename-beneficiary-sidesheet"
                data-ga-id="rename-beneficiary-sidesheet"
                data-ga-action="Open rename beneficiary dialog from sidesheet"
                onClick={() => setIsRenameOpen(true)}
                iconProps={{
                  Component: (props: ISVGIconProps) => (
                    <CloudFlareImage {...props} path={CloudflareAsset.Edit}/>
                  ),
                }}
              />
              <Button
                type="tertiary"
                size="small"
                className="mx-16"
                data-testid="delete-beneficiary-sidesheet"
                data-ga-id="delete-beneficiary-sidesheet"
                data-ga-action="Open delete beneficiary dialog from sidesheet"
                onClick={onDeleteClick}
                iconProps={{
                  Component: (props: ISVGIconProps) => (
                    <CloudFlareImage {...props} path={CloudflareAsset.Trash}/>
                  ),
                }}
              />
            </div>
          ) : null
        }
        title={beneficiary?.name}
        shouldRemoveCallback={false}
        closeOnOutsideClick={false}
      >
        <div className="beneficiary-detail-content">
          <div
            className="flexbox justify-content--between align-items--center beneficiary-switcher-wrapper mt-32 mb-24">
            <Text type="primary" weight="bolder">
              {t("beneficiary.createBeneficiary.isAvailableForOrgUsers")}
            </Text>
            <Switcher
              onClick={onChangeVisibility}
              selectedValue={isAvailableForOrgUsers}
              disabled={!isPrivate}
            />
          </div>
          <div className="mb-8">
            <Tab
              size="small"
              color="light"
              tabItems={tabItems}
              selectedValue={activeTab}
              onSelect={(value) => setActiveTab(value as string)}
            />
          </div>
          <div className="beneficiary-detail-list">
            {!activeItems.length ? (
              <div className="py-80 px-24">
                <EmptyOrErrorContent
                  isShowButton={false}
                  title={t(activeTab === AttachmentType.Account
                    ? "beneficiary.noAccounts"
                    : "beneficiary.noCards"
                  )}
                  subtitle={t(activeTab === AttachmentType.Account
                    ? "beneficiary.noAccountsSubtitle"
                    : "beneficiary.noCardsSubtitle"
                  )}
                />
              </div>
            ) : activeItems.map((item, index) => (
              <BeneficiaryAccountCardItem
                key={item.id}
                item={item}
                index={index}
                type={activeTab === AttachmentType.Account ? AttachmentType.Account : AttachmentType.Card}
                isPrivate={isPrivate}
                onTransfer={handleTransfer}
                onEdit={openAttachModal}
                onDelete={setAttachmentToDelete}
              />
            ))}
          </div>
          {isPrivate && (
            <div className="beneficiary-detail-footer">
              <div
                className="detail-attachment-area cursor-pointer mt-24 mb-24"
                onClick={() => openAttachModal()}
              >
                <Button
                  className="attachment-button rounded"
                  type="tertiary"
                  iconProps={{
                    Component: (props: ISVGIconProps) => (
                      <CloudFlareImage {...props} size="small" path={CloudflareAsset.Add}/>
                    ),
                  }}
                />
                <Text weight="semibold" className="mt-24 mb-8">
                  {t(`beneficiary.attachment.${activeTab}`)}
                </Text>
              </div>
            </div>
          )}
        </div>
      </SideSheet>
      <div onClick={(e) => e.stopPropagation()}>
        <DeleteAccountCardModal
          isOpen={!!attachmentToDelete}
          beneficiaryId={beneficiaryId}
          item={attachmentToDelete}
          isLast={beneficiary?.entries?.length === 1}
          type={activeTab === AttachmentType.Account ? AttachmentType.Account : AttachmentType.Card}
          onClose={() => setAttachmentToDelete(null)}
          onSuccess={() => {
            if (beneficiary?.entries?.length === 1) {
              refetchBeneficiariesList();
              onClose();
            } else {
              refetch();
            }
          }}
        />
      </div>
      <div onClick={(e) => e.stopPropagation()}>
        <DeleteBeneficiaryModal
          id={entryId}
          setId={setEntryId}
          refetch={handleDeleteSuccess}
          beneficiaryName={beneficiary?.name as string}
          isOpen={isDeleteOpen}
          setIsOpen={setIsDeleteOpen}
        />
      </div>
      <div onClick={(e) => e.stopPropagation()}>
        <RenameBeneficiaryModal
          id={beneficiaryId}
          isOpen={isRenameOpen}
          beneficiaryName={beneficiary?.name as string}
          onClose={() => setIsRenameOpen(false)}
          refetch={refetch}
        />
      </div>
      <div onClick={(e) => e.stopPropagation()}>
        <AttachModal
          isOpen={!!attachModalState}
          isEdit={attachModalState?.mode === AttachModalState.Edit}
          attachmentType={activeTab as AttachmentType}
          data={attachModalState?.mode === AttachModalState.Edit ? attachModalState.item : undefined}
          clientId={modalClientId}
          beneficiaryId={beneficiaryId}
          setClientId={setModalClientId}
          accounts={accountEntries}
          cards={cardEntries}
          onConfirm={handleModalConfirm}
          onClose={() => setAttachModalState(null)}
        />
      </div>
    </>
  );
};
